import React from 'react';
import { Code, Smartphone, Zap, Shield, Rocket, CheckCircle2, Brain, Database, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <div className="animate-fade-in bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white pt-24 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900/20 to-transparent"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-600/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              About <span className="gradient-text text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-sky-200">NyxSolutions</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed">
              An AI-driven product development studio building modern, scalable, and intelligent applications for businesses and institutions.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Empower organizations with intelligent software that enhances efficiency, reduces manual work, and accelerates growth.
              </p>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                NyxSolutions combines deep expertise in mobile development, backend engineering, AI integration, and product design to deliver solutions that solve real operational challenges across multiple industries.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                From school management to field sales automation, from hotel & restaurant operations to smart ride-sharing systems, and from advanced multi-tenant document management to conversational AI interfaces—NyxSolutions builds products that are practical, reliable, and ready for scale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What We Do</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end product engineering with AI-powered solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* End-to-End Engineering */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-brand-100 rounded-xl flex items-center justify-center mb-6">
                <Code className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">End-to-End Product Engineering</h3>
              <p className="text-gray-600 leading-relaxed">
                We design, develop, and deploy complete digital products—from mobile apps to enterprise SaaS platforms—using cutting-edge tools and modern cloud infrastructure.
              </p>
            </div>

            {/* AI-Powered Solutions */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Brain className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">AI-Powered Solutions</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We integrate advanced AI capabilities including:
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>Document OCR & metadata extraction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>Semantic search with vector embeddings</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>Intelligent recommendations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>Chat-based assistants (OpenAI + Gemini)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>Automation pipelines & smart workflows</span>
                </li>
              </ul>
            </div>

            {/* Industry-Focused Platforms */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6">
                <Layers className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Industry-Focused Platforms</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We build and maintain specialized applications:
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>AJWS School App</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>Carpool & Mobility System</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>Hotel & Restaurant Management</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>Gajkesari Sales App</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                  <span>AI Document Management System</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <p className="text-center text-gray-700 leading-relaxed">
              Across all products, we focus on <strong className="text-gray-900">clean UI/UX</strong>, <strong className="text-gray-900">robust architecture</strong>, <strong className="text-gray-900">secure APIs</strong>, and <strong className="text-gray-900">scalable cloud deployment</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Approach</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology for building successful products
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-12 h-12 bg-brand-600 rounded-full flex items-center justify-center text-white font-bold text-xl">1</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Discover & Design</h3>
              <p className="text-gray-600 leading-relaxed">
                Understanding your workflow, challenges, and business goals to craft the right solution.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">2</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Build & Integrate</h3>
              <p className="text-gray-600 leading-relaxed">
                Full-stack development powered by React, React Native, Node.js/Fastify, Supabase, PostgreSQL, Flask, and AI APIs.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl">3</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Deploy & Scale</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimized for performance, multi-tenancy, and future growth—whether on cloud or self-hosted.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl">4</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Support & Evolve</h3>
              <p className="text-gray-600 leading-relaxed">
                Continuous updates, feature upgrades, performance enhancements, and long-term partner support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why NyxSolutions Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why NyxSolutions?</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              What sets us apart in product development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <Zap className="w-8 h-8 text-brand-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">AI-first engineering</h3>
              <p className="text-slate-300 text-sm">Intelligent automation, embeddings, OCR, chatbots</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <Code className="w-8 h-8 text-brand-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">Full-stack expertise</h3>
              <p className="text-slate-300 text-sm">Frontend, backend, mobile, cloud, DevOps-lite</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <Database className="w-8 h-8 text-brand-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">Multi-tenant ready</h3>
              <p className="text-slate-300 text-sm">Org-level isolation, RBAC, secure storage</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <Rocket className="w-8 h-8 text-brand-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">Fast execution</h3>
              <p className="text-slate-300 text-sm">Rapid prototyping + production-grade delivery</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <Smartphone className="w-8 h-8 text-brand-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">Real business impact</h3>
              <p className="text-slate-300 text-sm">Apps used by schools, sales teams & organizations today</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <Shield className="w-8 h-8 text-brand-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">Modern UI/UX</h3>
              <p className="text-slate-300 text-sm">Clean, fast, and user-friendly interfaces</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Ready to Build Something Great?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Explore our products and see how we can help transform your operations.
          </p>
          <Link
            to="/products"
            className="btn-primary inline-flex items-center px-8 py-4 bg-brand-600 text-white font-semibold rounded-lg hover:bg-brand-700 shadow-lg shadow-brand-200"
          >
            View Our Products
            <Rocket className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;

